/**
 * Resource Bookings — online upsell / bookable-resource add-ons on event &
 * Pullman bookings (Guide Book, Pullman Ceramic Decoration, Activity Book, Treat
 * Bag, Gala T-Shirts, …). Second tab on the Retail & Tea Room Sales screen;
 * replaces Jason's "Bookable Resources" Power BI report.
 *
 * Reads GET /api/dashboards/sales/resource-bookings?from=&to= (cache.
 * ResourceBookings <- MStore.NYMR.vw_BookableResourcesSales). Smart filters
 * (date range, product, search) + CSV and PDF download.
 */
import { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "./resource-bookings.css";

interface RBRow {
  BookingDateTime: string | null;
  BookingDate: string | null;
  BookingRef: number | null;
  bkrResNo: number | null;
  Customer: string | null;
  Product: string | null;
  CapturedData: string | null;
  Qty: number;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function prettyDate(iso: string | null): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : iso;
}
function timeOf(iso: string | null): string {
  if (!iso) return "";
  const m = /T(\d{2}:\d{2})/.exec(iso) || /\s(\d{2}:\d{2})/.exec(iso);
  return m ? m[1] : "";
}
function prettyDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}
function todayIso(): string { return new Date().toISOString().slice(0, 10); }
function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10);
}

export function ResourceBookings() {
  const [from, setFrom] = useState<string>(() => addDaysIso(todayIso(), -7));
  const [to, setTo] = useState<string>(() => addDaysIso(todayIso(), 90));
  const [rows, setRows] = useState<RBRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);
  const [product, setProduct] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (from > to) { setError("'From' must be on or before 'To'."); setRows([]); return; }
    setLoading(true); setError(null);
    fetch(`/api/dashboards/sales/resource-bookings?from=${from}&to=${to}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Request failed (${res.status})`); }
        return res.json() as Promise<{ rows: RBRow[]; last_refresh?: string | null }>;
      })
      .then((data) => { if (cancelled) return; setRows(data.rows ?? []); setLastRefresh(data.last_refresh ?? null); })
      .catch((err) => { if (cancelled) return; setError(err instanceof Error ? err.message : String(err)); setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  const products = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) if (r.Product) s.add(r.Product);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (product && r.Product !== product) return false;
      if (!q) return true;
      return [r.Customer, r.BookingRef, r.Product, r.CapturedData].some((v) => v != null && String(v).toLowerCase().includes(q));
    });
  }, [rows, product, search]);

  const totalQty = useMemo(() => filtered.reduce((a, r) => a + (r.Qty ?? 0), 0), [filtered]);

  const canExport = !loading && !error && filtered.length > 0;

  return (
    <main className="app-main rb">
      <div className="rb-head">
        <div>
          <h1>Resource Bookings</h1>
          <p className="rb-sub">
            Online upsells &amp; bookable-resource add-ons on event and Pullman bookings — what was bought,
            who it's for and when. Filter, then download as CSV or PDF.
          </p>
        </div>
        <span className="rb-refresh-note">Last refreshed {prettyDateTime(lastRefresh)}.</span>
      </div>

      <div className="rb-toolbar">
        <label className="rb-field"><span>From</span>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="rb-field"><span>To</span>
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="rb-field"><span>Product</span>
          <select value={product} onChange={(e) => setProduct(e.target.value)}>
            <option value="">All products ({products.length})</option>
            {products.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="rb-search">
          <span className="rb-search-icon" aria-hidden="true">🔍</span>
          <input type="search" value={search} placeholder="Search customer, booking, note…" onChange={(e) => setSearch(e.target.value)} />
          {search && <button type="button" className="rb-search-clear" aria-label="Clear search" onClick={() => setSearch("")}>×</button>}
        </label>
        <span className="rb-count">
          {loading ? "Loading…" : `${filtered.length.toLocaleString()} row${filtered.length === 1 ? "" : "s"} · ${totalQty.toLocaleString()} item${totalQty === 1 ? "" : "s"}`}
        </span>
        <button type="button" className="rb-btn" onClick={() => downloadRbCsv(filtered, { from, to })} disabled={!canExport}>⬇ CSV</button>
        <button type="button" className="rb-btn" onClick={() => downloadRbPdf(filtered, { from, to })} disabled={!canExport}>🖨 PDF</button>
      </div>

      <div className="rb-table-wrap">
        {error ? (
          <div className="rb-empty rb-empty--error">Couldn’t load resource bookings: {error}</div>
        ) : loading && rows.length === 0 ? (
          <div className="rb-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="rb-empty">{rows.length === 0 ? "No resource bookings in this date range." : "No rows match your filters."}</div>
        ) : (
          <table className="rb-table">
            <thead>
              <tr>
                <th>Date</th><th>Time</th><th className="ta-right">Booking</th><th>Customer</th>
                <th>Product</th><th className="ta-right">Qty</th><th>Captured data</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={i}>
                  <td>{prettyDate(r.BookingDate)}</td>
                  <td>{timeOf(r.BookingDateTime)}</td>
                  <td className="ta-right">{r.BookingRef ?? ""}</td>
                  <td>{r.Customer ?? ""}</td>
                  <td>{r.Product ?? ""}</td>
                  <td className="ta-right">{r.Qty}</td>
                  <td className="rb-captured">{r.CapturedData ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}

// --- exports -----------------------------------------------------------------
function rbCells(r: RBRow): string[] {
  return [prettyDate(r.BookingDate), timeOf(r.BookingDateTime), r.BookingRef == null ? "" : String(r.BookingRef),
    r.Customer ?? "", r.Product ?? "", String(r.Qty ?? 0), r.CapturedData ?? ""];
}
const RB_HEAD = ["Date", "Time", "Booking", "Customer", "Product", "Qty", "Captured data"];

function downloadRbCsv(rows: RBRow[], range: { from: string; to: string }) {
  const esc = (s: string) => (/[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const lines = [RB_HEAD.join(",")];
  for (const r of rows) lines.push(rbCells(r).map(esc).join(","));
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `resource-bookings-${range.from}_to_${range.to}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

function downloadRbPdf(rows: RBRow[], range: { from: string; to: string }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const printedOn = new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
  const qty = rows.reduce((a, r) => a + (r.Qty ?? 0), 0);
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(11, 61, 46);
  doc.text("Retail & Tea Room — Resource Bookings", 10, 12);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(90, 90, 90);
  doc.text(`${range.from} to ${range.to}  ·  ${rows.length} row${rows.length === 1 ? "" : "s"}  ·  ${qty} items  ·  printed ${printedOn}`, 10, 17);
  autoTable(doc, {
    head: [RB_HEAD],
    body: rows.map(rbCells),
    startY: 21,
    margin: { left: 8, right: 8 },
    styles: { fontSize: 7.5, cellPadding: 1.3, overflow: "linebreak", valign: "top" },
    headStyles: { fillColor: [31, 77, 63], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [250, 248, 242] },
    columnStyles: { 2: { halign: "right", cellWidth: 22 }, 5: { halign: "right", cellWidth: 16 }, 6: { cellWidth: 70 } },
  });
  doc.save(`resource-bookings-${range.from}_to_${range.to}.pdf`);
}
